/**
 * The course on Desk, Outline mode (#1393 M2, contract 3 read map).
 *
 * Pure boxes over four reads: the course content (guide, topics, their
 * sessions, readings, guidance units and coursework), the material library
 * (syllabus and resources) and the Drive folder; Google Calendar is drawn
 * once by `TheCourse`, beside both modes. It never mounts
 * `CourseTopics` or any exact owner: those mount only when a visit enters
 * them. A resource joins a topic only through recorded relationships
 * (`unit_id` -> the content read's unit -> its session -> that session's
 * topic); a read that is missing groups nothing and never says "unplaced".
 */
import { useContext, useEffect, useRef, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { Bento } from "@/components/ui/box";
import { BentoVisit } from "@/components/ui/box-visit-context";
import { GoogleDriveMark } from "@/components/ui/google-drive-mark";
import type { Box, Exit, FactBox, ListBox, Read, VisitAddress } from "@/components/ui/box.schema";
import { a3Copy } from "@/components/assignments/a3-copy";
import { ApiError } from "@/lib/api/client";
import { courseMaterialsApi, type MaterialLibraryRead } from "@/lib/api/courseMaterials";
import type { ContentReading, ContentTopic, CourseContent } from "@/lib/api/courseContent";
import type { TopicSession } from "@/lib/api/courseTopics";
import { formatCourseInstant } from "@/lib/seat-workspace";
import { RESOURCES_SELECTORS } from "@/navigation/course-address";
import { courseSessionHref } from "@/navigation/course-workspace";
import { readTrail, searchWithTrail } from "@/lib/visit/address";
import { materialUnitOf, withMaterialUnit } from "../materials/material-unit";
import { contentRead, useCourseContent } from "../content/use-course-content";
import { useDriveBox } from "../course-page-boxes";
import { addressedPart, usePartTarget } from "../course-part";
import { useDeskMemory } from "./desk-memory";
import { OUTLINE_COPY, outlineResources, resourceVisit, topicOfUnit, workDestination, type OutlineResource } from "./outline-model";

const LOOSE = "not-in-a-topic";

type TopicRow = { kind: "topic"; topic: ContentTopic } | { kind: "loose" };

function today(timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function topicRange(topic: ContentTopic): string | null {
  if (!topic.starts_on) return null;
  const day = (value: string) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(`${value}T12:00:00Z`));
  return topic.ends_on && topic.ends_on !== topic.starts_on ? `${day(topic.starts_on)} to ${day(topic.ends_on)}` : day(topic.starts_on);
}

function sessionLine(session: TopicSession, timezone: string): string {
  const when = session.starts_at ? formatCourseInstant(session.starts_at, timezone, true) : null;
  return [`Session ${session.session_no}`, when, session.status === "cancelled" ? "Cancelled" : null].filter(Boolean).join(" · ");
}

function value<T>(read: Read<T>): T | null {
  return read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
}

export function CourseOutline({ courseId, courseHref, teaching, canEdit, termOpen, timezone }: {
  courseId: string;
  courseHref: string;
  teaching: boolean;
  canEdit: boolean;
  termOpen: boolean;
  timezone: string;
}) {
  const host = useContext(BentoVisit);
  const memory = useDeskMemory();
  const content = useCourseContent(courseId, teaching ? "draft-preview" : "learner");
  const library = useQuery({ queryKey: ["material-library", courseId], queryFn: () => courseMaterialsApi.getLibrary(courseId), retry: false });
  const drive = useDriveBox({ courseId, canEdit, termOpen, teaching, timezone });

  const outline = contentRead(content);
  const read = value(outline);
  const zone = read?.timezone ?? timezone;
  const libraryRefused = library.error instanceof ApiError && [401, 403, 404].includes(library.error.status);
  // A refusal withdraws what the library showed before, here and inside topics;
  // a transient failure keeps the known rows, marked stale in Resources.
  const known = libraryRefused ? undefined : library.data;
  const resources = known ? outlineResources(known, teaching) : null;
  const topicOf = read ? topicOfUnit(read) : null;
  // A resource is in a topic only when both reads place it there.
  // Resources is an addressed part (R15): its head takes arrival focus. A
  // stored material or Add material address is the part's to finish (rule 6):
  // once the library names the material, the arrival is replaced by its visit;
  // a material the library does not hold is unavailable; a failed read keeps
  // the address with Try again.
  const outlineRef = useRef<HTMLDivElement>(null);
  usePartTarget("resources", outlineRef, '[data-box-id="materials"] [data-box-head]');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const resourcesPart = addressedPart(params) === "resources";
  const askedMaterial = resourcesPart ? params.get("material") : null;
  const composing = resourcesPart && params.get("compose") === "material";
  const asked = askedMaterial && resources ? resources.find((resource) => resource.materialId === askedMaterial) ?? null : undefined;
  const addMaterial: VisitAddress = `material:${courseId}/new-course_content`;
  const arrivalVisit: VisitAddress | null = composing ? addMaterial : asked ? resourceVisit(courseId, asked) : null;
  const materialMissing = !composing && asked === null;
  useEffect(() => {
    if (!arrivalVisit) return;
    const next = new URLSearchParams(location.search);
    // The address consumes `unit` (rule 7); the new material's visit takes it
    // as its placement, which its editor validates against the course's
    // sessions before recording (R17, #1393 M4-A3).
    const unit = composing ? next.get("unit") : null;
    for (const key of RESOURCES_SELECTORS) next.delete(key);
    navigate({ pathname: location.pathname, search: searchWithTrail(next.toString(), [arrivalVisit]), hash: location.hash }, { replace: true, state: withMaterialUnit(location.state, unit) });
  }, [arrivalVisit, composing, navigate, location.pathname, location.search, location.hash, location.state]);
  // The handed unit is that one visit's starting placement: visit memory
  // carries state forward, so once the visit closes the unit is let go and a
  // later Add material starts unplaced.
  const unitLeft = materialUnitOf(location.state) !== undefined && !readTrail(location.search).includes(addMaterial);
  useEffect(() => {
    if (unitLeft) navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: withMaterialUnit(location.state, null) });
  }, [unitLeft, navigate, location.pathname, location.search, location.hash, location.state]);
  const placedIn = (topicId: string) => (resources && topicOf ? resources.filter((resource) => resource.role === "course_content" && topicOf(resource.unitId) === topicId) : []);

  const visit = (address: VisitAddress, box: string, row: string) => host?.visit(address, { box, row });
  const visitButton = (label: string, address: VisitAddress, box: string, row: string, key: string, mark?: ReactNode) => (
    <li key={key}><button type="button" className="text-left underline-offset-2 hover:underline focus-visible:underline" data-desk-visit={address} onClick={() => visit(address, box, row)}>{mark}{label}</button></li>
  );
  // A Drive file's mark, inline before its name so a long name wraps under it.
  const driveMark = (resource: OutlineResource) => resource.drive ? <GoogleDriveMark mimeType={resource.drive.mimeType} url={resource.drive.url} className="me-2 align-[-0.1875em]" /> : undefined;

  // Open topics come back as last left: on Return, and after a mode switch remounts Outline.
  const keptOpen = memory?.live.openTopics;
  const restoredOpen = new Set(keptOpen ?? []);
  const now = today(zone);
  const current = read?.topics.find((topic) => topic.starts_on && topic.starts_on <= now && (!topic.ends_on || topic.ends_on >= now))?.id ?? null;
  const looseSessions = read ? read.sessions.filter((session) => !session.topic_id) : [];
  const topicRead: Read<CourseContent> = outline.status === "failed" ? { ...outline, sentence: OUTLINE_COPY.failed }
    : outline.status === "stale" ? { ...outline, sentence: OUTLINE_COPY.stale }
      : outline.status === "ready" && !outline.value.topics.length && !outline.value.sessions.length ? { status: "empty", sentence: OUTLINE_COPY.noTopics, ...(teaching && canEdit ? { sub: "Add a topic to group the course's sessions and readings." } : {}) }
        : outline;

  const topicDetail = (row: TopicRow): ReactNode => {
    if (!read) return undefined;
    const key = row.kind === "topic" ? row.topic.id : LOOSE;
    const sessions = read.sessions.filter((session) => (row.kind === "topic" ? session.topic_id === row.topic.id : !session.topic_id));
    const readings = row.kind === "topic" ? read.readings.filter((reading) => reading.topic_id === row.topic.id) : [];
    const placed = row.kind === "topic" ? placedIn(row.topic.id) : [];
    const work = row.kind === "topic" && topicOf ? read.coursework.filter((item) => topicOf(item.unit_id) === row.topic.id) : [];
    const part = (heading: string, items: ReactNode[]) => items.length ? <div className="grid gap-1"><h4 className="text-meta font-medium text-muted-foreground">{heading}</h4><ul className="grid gap-1">{items}</ul></div> : null;
    return (
      <div className="grid gap-3" data-topic-detail={key}>
        {row.kind === "topic" && row.topic.purpose && <p className="whitespace-pre-wrap" dir="auto">{row.topic.purpose}</p>}
        {part("Sessions", sessions.map((session) => <li key={session.id}><Link to={courseSessionHref(courseHref, session.id)} className="underline-offset-2 hover:underline">{sessionLine(session, zone)}</Link></li>))}
        {part("Readings", readings.map((reading) => visitButton(reading.title, `reading:${courseId}/${reading.id}`, "course-topics", key, reading.id)))}
        {part("Resources", placed.map((resource) => visitButton(resource.title, resourceVisit(courseId, resource), "course-topics", key, resource.id, driveMark(resource))))}
        {part("Coursework", work.map((item) => {
          const destination = workDestination(courseId, courseHref, item);
          return "to" in destination
            ? <li key={item.id}><Link to={destination.to} className="underline-offset-2 hover:underline" dir="auto">{item.title}</Link></li>
            : visitButton(item.title, destination.visit, "course-topics", key, item.id);
        }))}
        {!sessions.length && !readings.length && !placed.length && !work.length && <p className="text-muted-foreground">Nothing is placed in this topic yet.</p>}
      </div>
    );
  };

  const teachingExits: Exit[] = [{ label: "Learner preview", visit: `course-preview:${courseId}/draft` }, ...(canEdit ? [{ label: "Add topic", visit: `topic:${courseId}/new` as VisitAddress }] : [])];
  const topics: ListBox<CourseContent, TopicRow> = {
    id: "course-topics", kind: "list", label: "Topics", job: "Find a topic and what is placed in it: its sessions, readings, resources and coursework.", owner: { system: "imam" },
    read: topicRead, fold: "never", detail: () => "",
    state: (current) => ({ class: "quiet", word: `${current.topics.length} ${current.topics.length === 1 ? "topic" : "topics"}` }),
    rows: (current) => [...current.topics.map((topic): TopicRow => ({ kind: "topic", topic })), ...(current.sessions.some((session) => !session.topic_id) ? [{ kind: "loose" } as TopicRow] : [])],
    row: {
      key: (row) => (row.kind === "topic" ? row.topic.id : LOOSE),
      title: (row) => (row.kind === "topic" ? row.topic.title : OUTLINE_COPY.looseTopic),
      facts: (row) => {
        // The bucket says how its sessions join a topic, so it never reads as content of its own.
        if (row.kind === "loose") return [`${looseSessions.length} ${looseSessions.length === 1 ? "session" : "sessions"}`, teaching && canEdit ? (read?.topics.length ? OUTLINE_COPY.looseJoin : OUTLINE_COPY.looseAddTopic) : null].filter(Boolean).join(" · ");
        const count = read?.sessions.filter((session) => session.topic_id === row.topic.id).length ?? 0;
        return [topicRange(row.topic), `${count} ${count === 1 ? "session" : "sessions"}`].filter(Boolean).join(" · ");
      },
      state: (row) => (row.kind === "topic" && row.topic.id === current ? { class: "waiting", word: "Current topic" } : null),
      // The bucket opens in place onto its sessions, each a link to its session page.
      act: (row) => (row.kind === "topic" ? { does: "visit", verb: "Open topic", visit: `topic:${courseId}/${row.topic.id}` } : { does: "open", verb: OUTLINE_COPY.looseOpen }),
      startsOpen: (row) => {
        const key = row.kind === "topic" ? row.topic.id : LOOSE;
        return keptOpen ? restoredOpen.has(key) : row.kind === "topic" && row.topic.id === current;
      },
      detail: topicDetail,
    },
    ...(teaching && read ? { exits: teachingExits.slice(0, 2) as [Exit] | [Exit, Exit] } : {}),
  };

  const guide: FactBox<CourseContent> = {
    id: "course-guide", kind: "fact", label: "Course guide", job: "Read how this course works, or prepare its guide.", owner: { system: "imam" },
    read: outline, fold: "choice",
    detail: (current) => (current.guide ? "" : teaching ? "Not published yet" : ""),
    prose: (current) => current.guide?.overview || (teaching ? OUTLINE_COPY.noGuideTeaching : OUTLINE_COPY.noGuideLearner),
    exits: [{ label: teaching ? "Prepare guide" : "Read guide", visit: `course-guide:${courseId}` }],
  };

  const topicTitle = new Map(read?.topics.map((topic) => [topic.id, topic.title]) ?? []);
  const readingsRead: Read<CourseContent> = outline.status === "ready" && !outline.value.readings.length ? { status: "empty", sentence: OUTLINE_COPY.noReadings } : outline;
  const readings: ListBox<CourseContent, ContentReading> = {
    id: "course-readings", kind: "list", label: "Readings", job: "Find every course reading and its topic when grouped.", owner: { system: "imam" },
    read: readingsRead, fold: "never", detail: () => "",
    state: (current) => ({ class: "quiet", word: `${current.readings.length} ${current.readings.length === 1 ? "reading" : "readings"}` }),
    rows: (current) => current.readings,
    row: {
      key: (reading) => reading.id,
      title: (reading) => reading.title,
      facts: (reading) => [reading.topic_id ? topicTitle.get(reading.topic_id) : "Course reading", reading.due_at ? `Before ${formatCourseInstant(reading.due_at, zone, true)}` : null, reading.optional ? "Optional" : null].filter(Boolean).join(" · "),
      act: (reading) => ({ does: "visit", verb: "Open reading", visit: `reading:${courseId}/${reading.id}` }),
    },
  };

  // Resources: the syllabus, course-wide resources and any resource whose
  // topic the content read cannot name. Without the content read nothing is
  // grouped, so every resource is listed here as the library reads it.
  const resourcesRead: Read<MaterialLibraryRead> = libraryRefused ? { status: "forbidden" }
    : known ? library.isError ? { status: "stale", last: known, readAt: known.read_at, sentence: OUTLINE_COPY.resourcesStale, refresh: () => void library.refetch() } : { status: "ready", value: known, readAt: known.read_at }
    : library.isError ? { status: "failed", sentence: OUTLINE_COPY.resourcesFailed, retry: () => void library.refetch() } : { status: "loading" };
  const listed = (resources ?? []).filter((resource) => resource.role === "syllabus" || !topicOf || !topicOf(resource.unitId));
  const resourceList: ListBox<MaterialLibraryRead, OutlineResource> = {
    id: "materials", kind: "list", label: "Resources", job: "Find the syllabus and the course's resources, and visit each one's owner.", owner: { system: "imam" },
    read: resourcesRead.status === "ready" && !listed.length ? { status: "empty", sentence: resources?.length ? "Every resource is placed in a topic above." : OUTLINE_COPY.noResources } : resourcesRead,
    fold: "never", detail: () => "",
    state: () => ({ class: "quiet", word: `${listed.length} ${listed.length === 1 ? "resource" : "resources"}` }),
    rows: () => listed,
    row: {
      key: (resource) => resource.id,
      title: (resource) => resource.title,
      mark: (resource) => resource.drive ? <GoogleDriveMark mimeType={resource.drive.mimeType} url={resource.drive.url} /> : undefined,
      facts: (resource) => [
        resource.role === "syllabus" ? a3Copy.syllabus : resource.kind,
        resource.role === "syllabus" ? null : resource.unitId ? "Placed in a session" : teaching ? OUTLINE_COPY.notPlaced : a3Copy.wholeCourse,
        teaching ? (resource.published ? a3Copy.published : a3Copy.draft) : null,
      ].filter(Boolean).join(" · "),
      act: (resource) => ({ does: "visit", verb: a3Copy.readReference, visit: resourceVisit(courseId, resource) }),
    },
    // Adding a resource is the material owner's visit, opened from its part
    // (R16, R17): the retired Materials view's Add actions live here now.
    ...(teaching && canEdit && termOpen && !libraryRefused ? { exits: [
      { label: a3Copy.addMaterial, visit: addMaterial },
      ...(resources?.some((resource) => resource.role === "syllabus") ? [] : [{ label: a3Copy.addSyllabus, visit: `material:${courseId}/new-syllabus` as VisitAddress }]),
    ] as [Exit] | [Exit, Exit] } : {}),
  };

  const boxes: Box[] = [
    ...(read ? [guide as Box] : []),
    ...(outline.status === "forbidden" ? [] : [topics as Box]),
    ...(read ? [readings as Box] : []),
    resourceList as Box,
    drive as Box,
  ];
  return (
    <div ref={outlineRef} className="contents">
      {outline.status === "forbidden" && <p className="py-3 text-ui text-muted-foreground" data-outline-refused>{OUTLINE_COPY.forbidden}</p>}
      {materialMissing && <p role="alert" className="py-3 text-ui text-muted-foreground" data-material-unavailable>{a3Copy.thisMaterialIsUnavailable}</p>}
      <Bento id={`course-outline-${courseId}`} journey="course-desk" zone={zone} boxes={boxes}
        onRowOpenChange={(box, key, open) => {
          if (box !== "course-topics" || !memory) return;
          const openNow = new Set(memory.live.openTopics ?? (current ? [current] : []));
          if (open) openNow.add(key); else openNow.delete(key);
          memory.live.openTopics = [...openNow];
        }} />
    </div>
  );
}
